import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import useEventListener from '../utils/useEventListener';
import closestLine from './closestLine';
import styles from './Gutter.module.scss';
import type QuillEditor from './QuillEditor';
import { getEditorControlsContainer } from './ReactQuill';
import ReorderGutterButton from './ReorderGutterButton';
import type { Blot } from './types';
import { useAppDispatch } from '../app/redux/useAppSelector';
import * as PostLayout from '../post/PostLayout';

const blotMatchers = [
  {
    component: ReorderGutterButton,
    matcher: (blot: Blot, readOnly: boolean) => {
      if (readOnly) return false;
      const { blotName } = blot.statics;
      switch (blotName) {
        case 'list':
        case 'embed':
        case 'card':
        case 'iframe':
        case 'video':
          return true;
        case 'image':
        case 'attachment':
          // @ts-expect-error
          return !blot.isUploading();
        default:
          return false;
      }
    },
  },
];

interface GutterProps {
  quill: QuillEditor;
  readOnly: boolean;
}

const Gutter = ({ quill, readOnly }: GutterProps) => {
  const dispatch = useAppDispatch();
  const [activeLine, setActiveLine] = useState<{
    blot: Blot;
    component: typeof blotMatchers[0]['component'];
  } | null>(null);
  const activeLineLocked = useRef(false);

  const [top, setTop] = useState(0);
  const lastBlot = useRef<Blot | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [gutterWidth, setGutterWidth] = useState<number | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      setGutterWidth(
        parseInt(getComputedStyle(containerRef.current).width, 10),
      );
    }
  }, []);

  useEventListener(getEditorControlsContainer(quill), 'mouseleave', () => {
    if (activeLineLocked.current) return;
    setActiveLine(null);
    lastBlot.current = null;
  });

  const setStateWithBlot = (blot: Blot | null) => {
    const matched =
      blot && blotMatchers.find(({ matcher }) => matcher(blot, readOnly));
    if (matched) {
      const rect = (blot.domNode as HTMLElement).getBoundingClientRect();
      const container = quill.root.getBoundingClientRect();
      setActiveLine({ blot, component: matched.component });
      setTop(rect.top - container.top);
    } else {
      setActiveLine(null);
    }
  };

  useEventListener(quill.root, 'mouseover', (e) => {
    if (activeLineLocked.current) return;
    if (e.target === quill.root) return;
    const blot = quill.scroll.find(e.target as Node, true);
    const lineBlot =
      blot && blot.scroll === quill.scroll ? closestLine(blot) : null;
    if (lineBlot === lastBlot.current) return;

    lastBlot.current = lineBlot;
    setStateWithBlot(lineBlot);
  });

  // When mouse is moving inside Gutter, get the line blot that
  // has the same top offset.
  useEventListener(quill.root, 'mousemove', (e) => {
    if (activeLineLocked.current) return;
    if (
      e.target !== e.currentTarget ||
      (gutterWidth && e.offsetX > gutterWidth)
    )
      return;
    const blot = quill.lineFromYOffset(e.clientY);
    if (blot) {
      setStateWithBlot(blot);
    }
  });

  return (
    <PostLayout.Gutter
      ref={containerRef}
      data-no-print
      className={classNames('editor-gutter', styles.container)}
    >
      {activeLine && (
        <div className={styles.button}>
          <activeLine.component
            quill={quill}
            dispatch={dispatch}
            // @ts-expect-error
            activeBlot={activeLine.blot}
            buttonTopOffset={top}
            onLockedChange={(locked) => {
              activeLineLocked.current = locked;

              // Reset active line when unlocked, as the mouse may have moved
              // out of the active line.
              if (!locked) setActiveLine(null);
            }}
          />
        </div>
      )}
    </PostLayout.Gutter>
  );
};

export default Gutter;
